import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Plus, Trash2, Save, Info } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const AnnualDepreciationSection = ({ clientId, year, onUpdated }) => {
  const [entries, setEntries] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadData();
  }, [clientId, year]);

  const loadData = async () => {
    try {
      const { data } = await axios.get(`${API}/clients/${clientId}/annual-depreciation?year=${year}`, { headers });
      setEntries(data.entries || []);
      setLoaded(true);
    } catch {
      setEntries([]);
      setLoaded(true);
    }
  };

  const addEntry = () => {
    setEntries([...entries, { account_code: "", account_name: "", annual_amount: 0 }]);
  };

  const removeEntry = (idx) => {
    setEntries(entries.filter((_, i) => i !== idx));
  };

  const updateEntry = (idx, field, value) => {
    const updated = [...entries];
    updated[idx] = { ...updated[idx], [field]: field === "annual_amount" ? parseFloat(value) || 0 : value };
    setEntries(updated);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/clients/${clientId}/annual-depreciation`, {
        year,
        entries: entries.filter(e => e.account_name && e.annual_amount),
      }, { headers });
      toast.success(data.message || "Ammortamenti salvati!");
      if (onUpdated) onUpdated();
    } catch (e) {
      toast.error("Errore: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const totalAnnual = entries.reduce((sum, e) => sum + (e.annual_amount || 0), 0);
  const monthlyTotal = totalAnnual / 12;

  const formatCurrency = (v) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(v);

  if (!loaded) return <div className="p-8 text-center">Caricamento...</div>;

  return (
    <Card>
      <CardHeader>
        <CardTitle data-testid="depreciation-title">Ammortamenti Annuali {year}</CardTitle>
        <CardDescription>
          Inserisci il valore annuale e il sistema lo ripartisce automaticamente su tutti i 12 mesi.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Info box */}
        <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-800">
          <Info className="h-5 w-5 mt-0.5 flex-shrink-0" />
          <div>
            <strong>Come funziona:</strong> Inserisci l'importo annuale totale per ogni voce di ammortamento.
            Il sistema dividera automaticamente per 12 e aggiornera tutti i mesi dell'anno {year} gia caricati.
          </div>
        </div>

        {/* Entries table */}
        <div className="rounded-lg border overflow-hidden">
          <table className="w-full text-sm" data-testid="depreciation-table">
            <thead className="bg-slate-100">
              <tr>
                <th className="text-left p-3 font-semibold w-32">Codice Conto</th>
                <th className="text-left p-3 font-semibold">Descrizione</th>
                <th className="text-right p-3 font-semibold w-44">Importo Annuale</th>
                <th className="text-right p-3 font-semibold w-40">Importo Mensile</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center p-6 text-muted-foreground">
                    Nessun ammortamento inserito. Clicca "Aggiungi" per iniziare.
                  </td>
                </tr>
              )}
              {entries.map((entry, idx) => (
                <tr key={idx} className="border-t hover:bg-slate-50">
                  <td className="p-2">
                    <Input
                      value={entry.account_code}
                      onChange={(e) => updateEntry(idx, "account_code", e.target.value)}
                      placeholder="es. 10.04"
                      className="h-8 text-sm"
                      data-testid={`dep-code-${idx}`}
                    />
                  </td>
                  <td className="p-2">
                    <Input
                      value={entry.account_name}
                      onChange={(e) => updateEntry(idx, "account_name", e.target.value)}
                      placeholder="es. Ammortamento immobilizzazioni materiali"
                      className="h-8 text-sm"
                      data-testid={`dep-name-${idx}`}
                    />
                  </td>
                  <td className="p-2">
                    <Input
                      type="number"
                      value={entry.annual_amount || ""}
                      onChange={(e) => updateEntry(idx, "annual_amount", e.target.value)}
                      placeholder="0"
                      className="h-8 text-sm text-right tabular-nums"
                      data-testid={`dep-amount-${idx}`}
                    />
                  </td>
                  <td className="p-2 text-right tabular-nums font-medium text-muted-foreground">
                    {formatCurrency((entry.annual_amount || 0) / 12)}
                  </td>
                  <td className="p-2">
                    <Button variant="ghost" size="sm" onClick={() => removeEntry(idx)} className="h-8 w-8 p-0 text-red-500 hover:text-red-700">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
            {entries.length > 0 && (
              <tfoot className="bg-slate-50 border-t-2">
                <tr>
                  <td colSpan={2} className="p-3 font-bold text-right">TOTALE</td>
                  <td className="p-3 text-right font-bold tabular-nums">{formatCurrency(totalAnnual)}</td>
                  <td className="p-3 text-right font-bold tabular-nums text-blue-700">{formatCurrency(monthlyTotal)}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* Actions */}
        <div className="flex justify-between">
          <Button variant="outline" onClick={addEntry} data-testid="add-depreciation-btn">
            <Plus className="h-4 w-4 mr-2" /> Aggiungi Voce
          </Button>
          <Button onClick={save} disabled={saving} data-testid="save-depreciation-btn">
            <Save className="h-4 w-4 mr-2" /> {saving ? "Salvataggio..." : "Salva e Distribuisci sui Mesi"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default AnnualDepreciationSection;
